import React, { createContext, useState, useContext, useEffect } from 'react';
import apiClient from '../api/client';
import { getCurrentTenant } from '../api/tenants';
import { setDisplayCurrency } from '../utils/format';

// DYN-311. The safe fallback whenever there is no tenant to price in, or the
// tenant lookup fails: no session, a failed /tenants/current, sign-out.
const DEFAULT_DISPLAY_CURRENCY = 'USD';

interface User {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  tenant_id: string;
  // DYN-69: collected by Settings > Profile and, until now, discarded.
  phone?: string;
  company_name?: string;
  timezone?: string;
  language?: string;
}

/**
 * The one place that decides whether a response body is a user. DYN-71.
 *
 * `User` above is a claim about what the server sends, and axios returns `any`,
 * so nothing checked that claim at the boundary. Every 200 was trusted, and a
 * 200 carrying something else is not hypothetical: any unknown non-API path on
 * this site is answered by the SPA catch-all with 200 and an HTML body, which
 * was measured on production. A routing change that stops /users/me reaching
 * the API therefore signs the user in as an HTML document, and the header's
 * `user.email.split('@')` throws on it. A thrown render is a blank page, not a
 * handled error.
 *
 * It checks `email` and nothing else about type, deliberately. That is the one
 * field this app reaches through, so it is the one that needs a guarantee, and
 * requiring the whole interface would sign everyone out the first time the
 * backend adds or renames an optional field, which is a worse failure than the
 * one being prevented.
 *
 * `id` is checked for presence but NOT for type. An earlier version required a
 * number, which asserts a serialisation format on no evidence: the only place
 * that reads it is `{user?.id}`, which never reaches through it, so if the
 * backend ever sent a UUID everyone would be signed out by the very check that
 * exists to avoid that. Presence still rejects a body that happens to carry an
 * email without being a user.
 *
 * Empty string is rejected rather than treated as present, because
 * `user.email[0].toUpperCase()` throws on '' before a `|| '?'` fallback can
 * help.
 */
export const asUser = (value: unknown): User | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Partial<User>;
  if (typeof candidate.email !== 'string' || candidate.email === '') return null;
  if (candidate.id === undefined || candidate.id === null) return null;
  return candidate as User;
};

interface AuthContextData {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  isDemo: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginDemo: () => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    // Optimistic first paint only — syncDemoStatus below replaces this with
    // the server's answer as soon as /users/me comes back.
    const storedIsDemo = localStorage.getItem('@AnalyticsBot:isDemo') === 'true';
    setIsDemo(storedIsDemo);
    fetchUserData();
  // Mount only, and it must stay that way: this is the session bootstrap, and
  // re-running it would re-issue /users/me on every render of the provider.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The server owns the demo flag; localStorage only caches it so the demo
   * banner does not flicker on load. It used to be the only source, and
   * nothing but logout() ever cleared it, so anyone who tried the demo and
   * later signed up in the same browser stayed flagged as a demo tenant and
   * kept being shown sample numbers. On any failure we fall back to "not a
   * demo account", because showing real data to a demo user is harmless and
   * showing sample data to a real one is the bug. See DYN-37.
   */
  const syncDemoStatus = async () => {
    try {
      const { data } = await apiClient.get('/demo/status');
      const demo = data?.is_demo === true;
      setIsDemo(demo);
      if (demo) {
        localStorage.setItem('@AnalyticsBot:isDemo', 'true');
      } else {
        localStorage.removeItem('@AnalyticsBot:isDemo');
      }
    } catch (error) {
      setIsDemo(false);
      localStorage.removeItem('@AnalyticsBot:isDemo');
    }
  };

  const fetchUserData = async () => {
    try {
      // The httpOnly cookie is sent automatically by the browser
      const response = await apiClient.get('/users/me');
      const identified = asUser(response.data);
      if (!identified) {
        // Fail closed. Not knowing who someone is has to read as signed out,
        // because the alternative is a session built on a body we cannot
        // parse, and the page it renders is blank.
        throw new Error('/users/me answered 200 with something that is not a user');
      }
      setUser(identified);
      await syncDemoStatus();

      // DYN-311. Own try/catch, separate from the /users/me one above: a
      // failure here must never sign anyone out or block sign-in, it just
      // means the app renders in USD, which is the safe default. Runs before
      // setLoading(false) so the first paint behind PrivateRoute is already
      // in the right currency rather than flashing USD and then switching.
      try {
        const tenant = await getCurrentTenant();
        setDisplayCurrency(tenant?.effective_display_currency || DEFAULT_DISPLAY_CURRENCY);
      } catch (error) {
        setDisplayCurrency(DEFAULT_DISPLAY_CURRENCY);
      }
    } catch (error) {
      // Cookie is missing or invalid — user is not authenticated
      setUser(null);
      // syncDemoStatus never runs on this path, so without this the optimistic
      // flag restored from localStorage above survives, and the next person to
      // sign in on this browser starts out flagged as a demo tenant. Same
      // failure DYN-37 was about, reached by the other branch. DYN-107.
      setIsDemo(false);
      localStorage.removeItem('@AnalyticsBot:isDemo');
      // DYN-311. No user means no tenant to price in, so the display
      // currency resets the same way the demo flag does above.
      setDisplayCurrency(DEFAULT_DISPLAY_CURRENCY);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);

    try {
      // Use URLSearchParams for form data (OAuth2 compatibility)
      const params = new URLSearchParams();
      params.append('username', email);
      params.append('password', password);

      await apiClient.post('/auth/login', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      // The httpOnly cookie is set by the backend response — fetch user data
      await fetchUserData();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginDemo = async () => {
    setLoading(true);

    try {
      // Retry up to 2 times to handle Render cold starts (timeout or transient 5xx)
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          // The response body is not used; the endpoint sets the auth cookie.
          await apiClient.post('/demo/initialize', {}, { timeout: 90000 });
          break;
        } catch (err: any) {
          const isTimeout = err.code === 'ECONNABORTED';
          const status = err.response?.status;
          // 503 is the demo gate deliberately refusing, not a cold start.
          // Retrying a decision wastes three requests per visit against the
          // endpoint's 5/hour limit, so a second visitor in the same hour
          // would have been rate limited instead of told why. The retry
          // exists for Render waking up, which surfaces as a timeout or any
          // other 5xx. Written as "every 5xx except 503" rather than naming
          // 502 and 504, because that is what the line below actually does.
          const isTransient = status >= 500 && status !== 503;
          if ((isTimeout || isTransient) && attempt < 2) {
            continue;
          }
          throw err;
        }
      }

      // Save demo flag (non-sensitive) in localStorage for UI purposes
      localStorage.setItem('@AnalyticsBot:isDemo', 'true');
      setIsDemo(true);

      // The httpOnly cookie is set by the backend response — fetch user data
      await fetchUserData();
    } catch (error) {
      console.error('Demo login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Clear the httpOnly cookie via backend endpoint
    apiClient.post('/auth/logout').catch(() => {
      // Best-effort — even if the call fails the cookie will expire
    });

    // Clear non-sensitive local state
    localStorage.removeItem('@AnalyticsBot:isDemo');
    setUser(null);
    setIsDemo(false);
    // DYN-311. The next sign-in decides the currency again; nothing should
    // carry a previous tenant's currency past sign-out.
    setDisplayCurrency(DEFAULT_DISPLAY_CURRENCY);
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const response = await apiClient.put('/users/me', userData);
      const updated = asUser(response.data);
      if (!updated) {
        // Different call, different correct answer. A malformed reply here
        // says the save may not have happened; it does not mean we have
        // stopped knowing who this is, so signing them out would be a worse
        // response than reporting the failure.
        throw new Error('/users/me answered 200 with something that is not a user');
      }
      setUser(updated);
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        loading,
        isDemo,
        login,
        loginDemo,
        logout,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
